import { type ComponentPropsWithoutRef, forwardRef, useMemo } from 'react';
import { composeVariants, cva, cx, type VariantProps } from '../../utils/cva';
import { AVATAR_SIZES } from '../Avatar/Avatar';

const skeletonBaseVariants = cva({
  base: [
    'bg-palette-gray-200',
    'before:inline-block',
    'align-top',
    'my-[calc((theme(spacing.line)-theme(spacing.font))/2)]',
    'rounded-xl',
  ],
  variants: {
    animated: {
      true: [
        'bg-gradient--90deg',
        'from-palette-gray-200',
        'from-10%',
        'via-palette-gray-50',
        'via-20%',
        'to-palette-gray-200',
        'to-30%',
        '[background-size:1000px_100%]',
        'animate-shimmer',
      ],
    },
  },
});

export const skeletonVariants = composeVariants(
  skeletonBaseVariants,
  cva({
    base: ['min-w-[50px]', 'min-h-[16px]'],
    variants: {
      layout: {
        inline: ['inline-block'],
        rectangle: ['block'],
      },
    },
  }),
);

export const SkeletonAvatarVariants = composeVariants(
  skeletonBaseVariants,
  cva({
    base: [
      'rounded-full',
      'block',
      'w-[var(--avatar-size,theme(spacing.12))]',
      'h-[var(--avatar-size,theme(spacing.12))]',
    ],
    variants: {
      avatarSize: AVATAR_SIZES,
    },
  }),
);

export const skeletonLinesVariants = composeVariants(
  skeletonBaseVariants,
  cva({
    base: ['w-full'],
    variants: {
      size: {
        xs: ['h-[16px]'],
        sm: ['h-[20px]'],
        md: ['h-[24px]'],
        lg: ['h-[28px]'],
      },
    },
  }),
);

export interface SkeletonProps
  extends ComponentPropsWithoutRef<'span'>,
    VariantProps<typeof skeletonVariants> {
  'data-testid'?: string;
}

export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(
  (props, ref) => {
    const {
      animated = true,
      className,
      layout = 'inline',
      'data-testid': dataTestId = 'skeleton',
      ...rest
    } = props;
    return (
      <span
        ref={ref}
        data-testid={dataTestId}
        className={cx(className, skeletonVariants({ layout, animated }))}
        {...rest}
      />
    );
  },
);

export interface SkeletonLinesProps
  extends ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof skeletonLinesVariants> {
  count?: number;
  'data-testid'?: string;
}

export function SkeletonLines(props: SkeletonLinesProps) {
  const {
    count = 1,
    size = 'md',
    'data-testid': dataTestId = 'skeleton-lines',
    ...rest
  } = props;

  return (
    <div {...rest}>
      {useMemo(
        () =>
          Array.from({ length: count }).map((_, i) => (
            <Skeleton
              data-testid={dataTestId}
              className={cx(skeletonLinesVariants({ size }))}
              key={String(i)}
            />
          )),
        [count, size, dataTestId],
      )}
    </div>
  );
}

export interface SkeletonAvatarProps
  extends ComponentPropsWithoutRef<'span'>,
    VariantProps<typeof SkeletonAvatarVariants> {
  'data-testid'?: string;
}

export const SkeletonAvatar = forwardRef<HTMLSpanElement, SkeletonAvatarProps>(
  (props, ref) => {
    const {
      animated = true,
      avatarSize,
      className,
      'data-testid': dataTestId = 'skeleton-avatar',
      ...rest
    } = props;
    return (
      <span
        ref={ref}
        data-testid={dataTestId}
        className={cx(
          className,
          SkeletonAvatarVariants({ avatarSize, animated }),
        )}
        {...rest}
      />
    );
  },
);
